import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Sticker,
  Truck,
  Sparkles,
  Loader2,
  Plus,
  Minus,
  PackageCheck,
  Pencil,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { HelpIcon } from "@/components/HelpIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import StickerOrderEditModal from "@/components/StickerOrderEditModal";
import { StickerReprintButton } from "@/components/StickerReprintButton";

const STICKERS_PER_PACK = 25;
const PACK_PRICE = 25;

export default function Stickers() {
  const { activeProperty, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [busy, setBusy] = useState(false);
  const [shipping, setShipping] = useState({
    full_name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
  });
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const canceled = params.get("canceled");

  const load = useCallback(async () => {
    if (!activeProperty) return;
    try {
      const { data } = await api.get(`/stickers/orders?property_id=${activeProperty.id}`);
      setOrders(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [activeProperty]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (canceled) {
      toast.info("Checkout canceled.");
      params.delete("canceled");
      setParams(params, { replace: true });
    }
  }, [canceled, params, setParams]);

  // Poll after returning from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled || attempts >= 6) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Sticker order paid — shipping soon!");
          params.delete("session_id");
          setParams(params, { replace: true });
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired");
          params.delete("session_id");
          setParams(params, { replace: true });
          return;
        }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [sessionId]);

  const order = async () => {
    for (const f of ["full_name", "address", "city", "state", "zip"]) {
      if (!shipping[f]) {
        toast.error(`Please fill the ${f.replace("_", " ")}`);
        return;
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/stickers/order", {
        property_id: activeProperty.id,
        quantity,
        shipping,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.success("Order placed");
        load();
        setShipping({ full_name: "", address: "", city: "", state: "", zip: "", phone: "" });
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="mono-tiny text-[#71717A]">Sticker packs</div>
        <div className="flex items-center gap-2 mt-2">
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
            Order printed QR stickers
          </h1>
          <HelpIcon step={3} label="Show tutorial · stickers" />
        </div>
        <p className="text-[#71717A] text-sm mt-1">
          Weatherproof. 2.5" × 2.5". Each pack contains 25 stickers ready to apply on vehicles.
        </p>
      </div>

      {/* Pricing banner — always $25/pack in the new pricing model */}
      <div
        className="bg-[#0F8F4F]/5 border border-[#0F8F4F]/20 rounded-xl p-6 flex items-center justify-between flex-wrap gap-4"
        data-testid="stickers-pricing-banner"
      >
        <div>
          <div className="mono-tiny text-[#0F8F4F]">Pack pricing</div>
          <div className="font-display font-bold text-lg mt-1">
            $25 per pack of 25 stickers
          </div>
          <div className="text-sm text-[#71717A] mt-1">
            Weatherproof printed QR stickers shipped within 3–5 business days.
          </div>
        </div>
        <Sticker className="w-8 h-8 text-[#0F8F4F]" strokeWidth={1.5} />
      </div>

      {/* Printer reprint quick-action — separate $50 flat charge that
          notifies support@scanmint.app + logs a SuperAdmin inbox row. */}
      {activeProperty && (
        <div
          className="bg-white border-2 border-dashed border-[#0F8F4F]/40 rounded-xl p-5 flex items-center justify-between flex-wrap gap-4"
          data-testid="stickers-reprint-card"
        >
          <div>
            <div className="mono-tiny text-[#0F8F4F]">Need a fresh print run?</div>
            <div className="font-display font-bold text-base mt-1">
              Request a printer reprint · $50
            </div>
            <div className="text-sm text-[#71717A] mt-1 max-w-xl">
              Faded stickers, updated branding, or a growing property? Pay $50
              and our print team will kick off a new run and email you tracking
              once shipped.
            </div>
          </div>
          <StickerReprintButton
            propertyId={activeProperty.id}
            testId="stickers-page-reprint-btn"
          />
        </div>
      )}

      {/* Order form */}
      <div className="bg-white border border-[#E4E4E7] rounded-xl p-6 md:p-8 space-y-6">
        <div className="flex items-center gap-3">
          <Sticker className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.5} />
          <div>
            <div className="mono-tiny text-[#71717A]">Order</div>
            <div className="font-display font-bold text-xl">How many packs?</div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            data-testid="stickers-qty-minus"
          >
            <Minus className="w-4 h-4" />
          </Button>
          <div
            className="font-display text-4xl font-bold w-16 text-center"
            data-testid="stickers-qty"
          >
            {quantity}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setQuantity((q) => q + 1)}
            data-testid="stickers-qty-plus"
          >
            <Plus className="w-4 h-4" />
          </Button>
          <div className="ml-auto text-right">
            <div className="mono-tiny text-[#71717A]">Total</div>
            <div className="font-display text-2xl font-bold" data-testid="stickers-total">
              ${(quantity * PACK_PRICE).toFixed(2)}
            </div>
            <div className="mono-tiny text-[#71717A]">
              {quantity * STICKERS_PER_PACK} stickers
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Full name">
            <Input
              value={shipping.full_name}
              onChange={(e) => setShipping({ ...shipping, full_name: e.target.value })}
              className="h-11"
              data-testid="stickers-name"
            />
          </Field>
          <Field label="Phone (optional)">
            <Input
              value={shipping.phone}
              onChange={(e) => setShipping({ ...shipping, phone: e.target.value })}
              className="h-11"
              data-testid="stickers-phone"
            />
          </Field>
          <Field label="Street address">
            <Input
              value={shipping.address}
              onChange={(e) => setShipping({ ...shipping, address: e.target.value })}
              className="h-11"
              data-testid="stickers-address"
            />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="City">
              <Input
                value={shipping.city}
                onChange={(e) => setShipping({ ...shipping, city: e.target.value })}
                className="h-11"
                data-testid="stickers-city"
              />
            </Field>
            <Field label="State">
              <Input
                value={shipping.state}
                onChange={(e) => setShipping({ ...shipping, state: e.target.value })}
                className="h-11"
                data-testid="stickers-state"
              />
            </Field>
            <Field label="ZIP">
              <Input
                value={shipping.zip}
                onChange={(e) => setShipping({ ...shipping, zip: e.target.value })}
                className="h-11"
                data-testid="stickers-zip"
              />
            </Field>
          </div>
        </div>

        <Button
          onClick={order}
          disabled={busy}
          className="h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B] w-full md:w-auto"
          data-testid="stickers-order-btn"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Truck className="w-4 h-4 mr-2" />
          )}
          {`Pay $${(quantity * PACK_PRICE).toFixed(2)} & ship`}
        </Button>
      </div>

      {/* Order history */}
      <div className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E4E4E7] flex items-center gap-2">
          <PackageCheck className="w-4 h-4 text-[#0F8F4F]" strokeWidth={1.5} />
          <div className="mono-tiny text-[#71717A]">Order history ({orders.length})</div>
        </div>
        {orders.length === 0 ? (
          <div className="p-6 text-sm text-[#71717A]">No orders yet.</div>
        ) : (
          <div className="divide-y divide-[#E4E4E7]">
            {orders.map((o) => {
              const fulfillment = o.order_status || "processing";
              const fulfillmentClass =
                fulfillment === "delivered"
                  ? "bg-[#00C16E]/10 text-[#008A4F] border-[#00C16E]/30"
                  : fulfillment === "shipped"
                  ? "bg-[#0F8F4F]/10 text-[#0F8F4F] border-[#0F8F4F]/30"
                  : fulfillment === "cancelled"
                  ? "bg-[#EF4444]/10 text-[#B91C1C] border-[#EF4444]/30"
                  : "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]";
              const canEdit =
                user?.role === "super_admin" || user?.role === "property_manager";
              return (
                <div
                  key={o.id}
                  className="p-5 flex items-center justify-between flex-wrap gap-3"
                  data-testid={`sticker-order-${o.id}`}
                >
                  <div>
                    <div className="font-medium">
                      {o.quantity} pack{o.quantity > 1 ? "s" : ""} · {o.stickers_total} stickers
                    </div>
                    <div className="mono-tiny text-[#71717A]">
                      {new Date(o.created_at).toLocaleString()} · {o.shipping?.city || "—"},{" "}
                      {o.shipping?.state || ""}
                    </div>
                    {o.tracking_number && (
                      <div className="mono-tiny text-[#0F8F4F] mt-1">
                        {o.carrier ? `${o.carrier} · ` : ""}Track: {o.tracking_number}
                      </div>
                    )}
                    {o.admin_notes && (
                      <div className="mono-tiny text-[#71717A] mt-1 italic">
                        Note: {o.admin_notes}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase border ${fulfillmentClass}`}
                      data-testid={`sticker-fulfillment-${o.id}`}
                    >
                      {fulfillment}
                    </span>
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase border ${
                        o.status === "paid" || o.status === "included"
                          ? "bg-[#00C16E]/10 text-[#008A4F] border-[#00C16E]/30"
                          : "bg-[#F4F4F5] text-[#71717A] border-[#E4E4E7]"
                      }`}
                    >
                      {o.status.replace("_", " ")}
                    </span>
                    <div className="font-display font-bold ml-1">
                      {o.amount === 0 ? "FREE" : `$${o.amount.toFixed(2)}`}
                    </div>
                    {canEdit && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditing(o)}
                        data-testid={`sticker-edit-${o.id}`}
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {editing && (
        <StickerOrderEditModal
          order={editing}
          open={!!editing}
          onClose={() => setEditing(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="mono-tiny text-[#71717A]">{label}</Label>
      {children}
    </div>
  );
}
